import React from 'react';
import * as S from './styles';

import { IProps } from './types';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { ADDRESS_DELETE, CUSTOMER_UPDATE_ADDRESS_NICKNAME, SET_DEFAULT_ADDRESS } from '@molecules/AddressCard/queries';
import { addressDelete } from '@molecules/AddressCard/__generated__/addressDelete';
import { useAlert } from 'react-alert';

import TrashAlt from '../../../images/icons/trash-alt-solid.svg';
import styled from 'styled-components';
import { customerUpdateAddressNickname } from './__generated__/customerUpdateAddressNickname';
import { customerSetDefaultAddress } from '@organisms/CheckoutInformation/__generated__/customerSetDefaultAddress';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@material-ui/core';
import { setDefaultAddress } from './__generated__/setDefaultAddress';
import { meForSettingDefaultAddress } from '@src/components/organisms/CheckoutInformation/__generated__/meForSettingDefaultAddress';
import {
  CUSTOMER_SET_DEFAULT_ADDRESS,
  ME_FOR_SETTING_DEFAULT_ADDRESS,
} from '@src/components/organisms/CheckoutInformation/queries';
import {
  ADDRESS_NICKNAME_REGEX,
  DISALLOWED_ADDRESS_NICKNAME_REGEX,
  DO_NOT_ALLOW_CONSECUTIVE_SPACE,
  ADDRESS_NICKNAME_REGEX_MAX_LENGTH,
} from '@src/utils/constants';

const AddressCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  padding-bottom: 18px;
`;

const AddressNicknameButton = styled.button`
  color: #ff3399;
  font-weight: bold;
`;

const AddressDeleteButton = styled.button``;

const AddressCardButtonBox = styled.div`
  text-align: center;
  padding-bottom: 24px;
`;

const AddressCard: React.FC<IProps> = ({ address, refetch }: IProps) => {
  const alert = useAlert();

  const [deleteAddress] = useMutation<addressDelete>(ADDRESS_DELETE, {
    onCompleted: (data) => {
      if (!data.addressDelete.errors.length) {
        alert.show('Address deleted successfully.');
        refetch();
      }
    },
  });

  const [updateAddressNickname] = useMutation<customerUpdateAddressNickname>(CUSTOMER_UPDATE_ADDRESS_NICKNAME, {
    onCompleted: (data) => {
      if (!data.addressUpdate.errors.length) {
        alert.show('Success changed Address Nickname');
        refetch();
      }
    },
  });

  const [updateSetDefaultAddress] = useMutation<setDefaultAddress>(SET_DEFAULT_ADDRESS, {
    onCompleted: (data) => {
      if (!data.customerSetDefaultAddress.errors.length) {
        alert.show('Success change Default Address');
        window.location.reload();
      }
    },
  });

  const handleClickDelete = (e: any) => {
    deleteAddress({
      variables: {
        id: address.id,
      },
    });
  };

  const {
    data: defaultShippingAddressData,
    error: defaultShippingAddressError,
    loading: defaultShippingAddressLoading,
  } = useQuery<meForSettingDefaultAddress>(ME_FOR_SETTING_DEFAULT_ADDRESS, {
    onCompleted: (data) => {
      if (!data?.me?.defaultShippingAddress) {
        customerSetDefaultAddress({
          variables: {
            id: address.id,
            type: 'SHIPPING',
          },
        });
      }
    },
  });
  const [customerSetDefaultAddress] = useMutation<customerSetDefaultAddress>(CUSTOMER_SET_DEFAULT_ADDRESS);

  const [open, setOpen] = React.useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const [addressNickname, setAddressNickname] = React.useState(address.addressNickname || '');

  const handleChangeAddressNickname = (value: string) => {
    let filteredValue = value.replace(DISALLOWED_ADDRESS_NICKNAME_REGEX, '');

    filteredValue = filteredValue.replace(DO_NOT_ALLOW_CONSECUTIVE_SPACE, ' ');

    if (filteredValue.length > ADDRESS_NICKNAME_REGEX_MAX_LENGTH) {
      filteredValue = filteredValue.slice(0, ADDRESS_NICKNAME_REGEX_MAX_LENGTH);
    }

    setAddressNickname(filteredValue);
  };

  const handleClickUpdateNickname = (event: React.MouseEvent<HTMLButtonElement>) => {
    const trimmedAddressNickname = addressNickname.trim();

    if (trimmedAddressNickname === '') {
      alert.show('Please enter a nickname.');
      return;
    }

    if (!ADDRESS_NICKNAME_REGEX.test(trimmedAddressNickname)) {
      alert.show("Alphabet, number and '-', '_' characters allowed");
      return;
    }

    updateAddressNickname({
      variables: {
        id: address.id,
        input: {
          country: 'IN',
          addressNickname: trimmedAddressNickname,
        },
      },
    });

    setAddressNickname(addressNickname.trim());
    setOpen(false);
  };

  const handleClickDefaultAddress = (event: any) => {
    updateSetDefaultAddress({
      variables: {
        id: address.id,
      },
    });
  };

  return (
    (address && (
      <S.Wrapper>
        <S.AddressCard>
          <>
            <AddressCardHeader>
              <AddressNicknameButton onClick={handleClickOpen}>
                {address.addressNickname == '' ? 'Enter new address Nickname' : address.addressNickname}
              </AddressNicknameButton>

              <AddressDeleteButton onClick={handleClickDelete}>
                <img src={TrashAlt} width={15} />
              </AddressDeleteButton>

              <Dialog open={open} onClose={handleClose} aria-labelledby="form-dialog-title">
                <DialogTitle id="form-dialog-title">Enter new address nickname</DialogTitle>
                <DialogContent>
                  <TextField
                    autoFocus
                    margin="dense"
                    id="addressNickname"
                    label="Address Nickname"
                    type="text"
                    fullWidth
                    value={addressNickname}
                    onChange={(e) => handleChangeAddressNickname(e.target.value)}
                  />
                </DialogContent>
                <DialogActions>
                  <Button onClick={handleClose} color="primary">
                    Cancel
                  </Button>
                  <Button onClick={handleClickUpdateNickname} color="primary">
                    Change
                  </Button>
                </DialogActions>
              </Dialog>
            </AddressCardHeader>
            <p>
              {address.firstName} {address.lastName}
            </p>
            <p>{address.streetAddress1}</p>
            <p>{address.streetAddress2}</p>
            <p>{address.city}</p>
            <p>{address.countryArea}</p>
            <p>{address.phone}</p>
            <AddressCardButtonBox>
              {defaultShippingAddressData?.me?.defaultShippingAddress?.id !== address.id ? (
                <Button variant="contained" color="secondary" onClick={handleClickDefaultAddress}>
                  Set default shipping address
                </Button>
              ) : (
                <Button variant="contained" color="secondary" disabled>
                  Default shipping address
                </Button>
              )}
            </AddressCardButtonBox>
          </>
        </S.AddressCard>
      </S.Wrapper>
    )) ||
    null
  );
};

export { AddressCard };
